/**
 * BillingSection — rich billing panel for the settings page.
 *
 * Shows plan card with pricing/period info, usage breakdown,
 * included features, and upgrade/manage actions.
 * All data derived from the PLANS config + Convex queries.
 */
import * as React from 'react';
import {
  ArrowSquareOut,
  CircleNotch,
  Lightning,
  CalendarBlank,
  Crown,
  Sparkle,
  CheckCircle,
  LockKey,
} from '@phosphor-icons/react';
import { Badge, Button } from '@fragments-sdk/ui';
import { useQuery, useAction } from 'convex/react';
import { api } from '@/convex';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import {
  PLANS,
  ENTITLEMENT_AREAS,
  PERIODIC_QUOTAS,
  QUOTA_LABELS,
  getQuota,
  getPlanEntitlementsByArea,
  getPlanHighlights,
  formatQuotaLimit,
  type PlanId,
  type PlanEntitlement,
} from '../../../convex/billing/plans';
import { UsageMeter } from './UsageMeter';
import { PricingTable } from './PricingTable';
import s from './billing.module.scss';

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface BillingSectionProps {
  plan: PlanId;
  isAdmin: boolean;
}

export function BillingSection({ plan, isAdmin }: BillingSectionProps) {
  const { orgId } = useOrgContext();
  const { projects } = useProjectContext();
  const members = useQuery(
    api.memberships.listByOrg,
    orgId ? { orgId } : 'skip',
  );
  const subscription = useQuery(
    api.stripe.queries.getSubscriptionForOrg,
    orgId && isAdmin ? { orgId } : 'skip',
  );
  const usageSummary = useQuery(
    api.billing.usage.getUsageSummary,
    orgId && isAdmin ? { orgId } : 'skip',
  );
  const createCheckout = useAction(api.stripe.actions.createCheckoutSession);
  const createPortal = useAction(api.stripe.actions.createPortalSession);

  const [loading, setLoading] = React.useState(false);
  const [showPricing, setShowPricing] = React.useState(plan === 'free');

  const memberCount = members?.length ?? 0;
  const repositoryCount = projects.length;

  async function handleUpgrade(planId: PlanId, interval: 'monthly' | 'annual') {
    if (!orgId) return;
    setLoading(true);
    try {
      const result = await createCheckout({ orgId, planId, interval });
      if (result.url) {
        window.location.href = result.url;
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleManage() {
    if (!orgId) return;
    setLoading(true);
    try {
      const result = await createPortal({ orgId });
      if (result.url) {
        window.location.href = result.url;
      }
    } finally {
      setLoading(false);
    }
  }

  const planConfig = PLANS[plan];
  const isPaid = plan !== 'free';
  const isTrialing = subscription?.status === 'trialing';
  const planHighlights = getPlanHighlights(plan);
  const entitlementsByArea = getPlanEntitlementsByArea(plan);

  return (
    <div className={s.billingPage}>
      {/* ── Plan card ── */}
      <div className={s.planCard} data-tier={plan}>
        <div className={s.planCardHeader}>
          <div className={s.planCardTitle}>
            <span className={s.planCardEyebrow}>Current plan</span>
            <span className={s.planCardName}>
              <Crown
                size={16}
                weight="fill"
                className={s.planCardIcon}
                aria-hidden
              />
              {planConfig.label}
            </span>
            <span className={s.planCardTagline}>{planConfig.tagline}</span>
          </div>

          <div className={s.planCardMeta}>
            {isPaid && subscription && (
              <div className={s.planCardMetaItem}>
                <span>
                  {subscription.seatCount} seat
                  {subscription.seatCount !== 1 ? 's' : ''}
                </span>
              </div>
            )}
            {subscription && isTrialing && subscription.trialEnd && (
              <div className={s.planCardMetaItem} data-trial>
                <Lightning size={12} weight="fill" />
                <span>Trial ends {formatDate(subscription.trialEnd)}</span>
              </div>
            )}
            {subscription && !isTrialing && (
              <div className={s.planCardMetaItem}>
                <CalendarBlank size={12} />
                <span>
                  {subscription.cancelAtPeriodEnd
                    ? `Cancels ${formatDate(subscription.currentPeriodEnd)}`
                    : `Renews ${formatDate(subscription.currentPeriodEnd)}`}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={s.planHighlightList} aria-label="Current plan summary">
          {planHighlights.slice(0, 4).map((highlight) => (
            <span key={highlight} className={s.planHighlight}>
              <CheckCircle size={13} weight="fill" />
              {highlight}
            </span>
          ))}
        </div>

        {/* Actions */}
        {isAdmin && (
          <div className={s.planCardActions}>
            {isPaid ? (
              <>
                <Button
                  type="button"
                  variant="outlined"
                  size="sm"
                  onClick={() => setShowPricing((v) => !v)}
                >
                  <Sparkle size={13} weight="fill" />
                  {showPricing ? 'Hide comparison' : 'Compare tiers'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleManage}
                  disabled={loading}
                >
                  {loading ? (
                    <CircleNotch size={13} className={s.spinIcon} />
                  ) : (
                    <ArrowSquareOut size={13} />
                  )}
                  Manage subscription
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setShowPricing((v) => !v)}
              >
                <Sparkle size={13} weight="fill" />
                {showPricing ? 'Hide plans' : 'Compare plans'}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* ── Pricing table ── */}
      {showPricing && (
        <PricingTable
          currentPlan={plan}
          onSelectPlan={isAdmin ? handleUpgrade : undefined}
          disabledReason="Ask an admin"
        />
      )}

      {/* ── Usage ── */}
      <div className={s.billingSection}>
        <div className={s.usageSectionHeader}>
          <p className={s.billingSectionTitle}>Usage</p>
          <span className={s.usagePeriodBadge}>This period</span>
        </div>
        <div className={s.usageGrid}>
          <UsageMeter
            label="Team members"
            current={memberCount}
            limit={getQuota(plan, 'maxMembers')}
          />
          <UsageMeter
            label="Repositories"
            current={repositoryCount}
            limit={getQuota(plan, 'maxProjects')}
          />
          {/* Periodic (AI) usage from usageMeters table */}
          {usageSummary
            ? usageSummary.map((meter) => (
                <UsageMeter
                  key={meter.quota}
                  label={meter.label}
                  current={meter.current}
                  limit={meter.limit}
                />
              ))
            : PERIODIC_QUOTAS.map((quota) => (
                <UsageMeter
                  key={quota}
                  label={QUOTA_LABELS[quota]}
                  current={0}
                  limit={getQuota(plan, quota)}
                />
              ))}
        </div>
      </div>

      {/* ── Plan entitlements ── */}
      <div className={s.billingSection}>
        <div className={s.entitlementHeader}>
          <div>
            <p className={s.billingSectionTitle}>Plan entitlements</p>
            <p className={s.usageSectionDesc}>
              The same source of truth powers product gates, upgrade prompts,
              and CI behavior.
            </p>
          </div>
          <Badge variant={isPaid ? 'success' : 'outline'} size="sm">
            {planConfig.label}
          </Badge>
        </div>
        <div className={s.entitlementGrid}>
          {ENTITLEMENT_AREAS.map((area) => {
            const items = entitlementsByArea[area.id].filter(
              shouldShowEntitlement,
            );
            if (items.length === 0) return null;
            return (
              <section key={area.id} className={s.entitlementGroup}>
                <div className={s.entitlementGroupHeader}>
                  <p>{area.label}</p>
                  <span>{area.description}</span>
                </div>
                <div className={s.entitlementList}>
                  {items.map((item) => (
                    <EntitlementRow
                      key={`${item.kind}:${item.key}`}
                      item={item}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function shouldShowEntitlement(item: PlanEntitlement): boolean {
  if (item.kind === 'quota') return item.key !== 'maxRepoBindings';
  return true;
}

function EntitlementRow({ item }: { item: PlanEntitlement }) {
  if (item.kind === 'quota') {
    return (
      <div className={s.entitlementRow}>
        <CheckCircle size={14} weight="fill" className={s.featureIcon} />
        <div className={s.entitlementCopy}>
          <span className={s.entitlementLabel}>{item.label}</span>
          <span className={s.entitlementDesc}>{item.description}</span>
        </div>
        <span className={s.entitlementValue}>
          {formatQuotaLimit(item.limit)}
        </span>
      </div>
    );
  }

  const requiredPlan =
    item.requiredPlan && !item.enabled ? PLANS[item.requiredPlan].label : null;

  return (
    <div className={s.entitlementRow} data-locked={!item.enabled || undefined}>
      {item.enabled ? (
        <CheckCircle size={14} weight="fill" className={s.featureIcon} />
      ) : (
        <LockKey size={14} className={s.featureIconLocked} />
      )}
      <div className={s.entitlementCopy}>
        <span className={s.entitlementLabel}>{item.label}</span>
        <span className={s.entitlementDesc}>{item.description}</span>
      </div>
      <span className={s.entitlementValue}>
        {item.enabled ? 'Included' : (requiredPlan ?? 'Locked')}
      </span>
    </div>
  );
}
